import { useEffect, useState } from 'react'
import { useApp } from '../context/AppContext'

/**
 * Hook personalizado para gestionar la lógica de un tablero Kanban.
 *
 * @hook
 * @param {Array} tickets - Lista de tickets que serán distribuidos en columnas
 *
 * @returns {Object} Estado y handlers del Kanban
 * @returns {Object} board - Objeto donde cada key es un id de columna y su valor es un array de tickets
 * @returns {Array} columns - Lista de estados (columnas) obtenidos desde la base de datos
 * @returns {boolean} loading - Indica si las columnas se están cargando
 * @returns {Function} handleDragStart - Maneja el inicio del drag de un ticket
 * @returns {Function} handleDrop - Maneja el drop de un ticket en una columna
 * @returns {Function} reloadColumns - Recarga manual de columnas
 *
 * @description
 * Este hook:
 * - Obtiene los estados (`Status`) desde Supabase
 * - Construye dinámicamente el board agrupando tickets por `statusId`
 * - Permite mover tickets entre columnas (drag & drop)
 * - Persiste el cambio de estado en la base de datos
 *
 * @example
 * const {
 *   board,
 *   columns,
 *   loading,
 *   handleDragStart,
 *   handleDrop
 * } = useKanban(tickets)
 *
 * @dependency
 * Requiere que `useApp` provea una instancia válida de Supabase
 */
export const useKanban = (tickets = []) => {
  /** Cliente de Supabase desde contexto */
  const { supabase } = useApp()

  /** @type {[Object, Function]} Estado del tablero Kanban */
  const [board, setBoard] = useState({})

  /** @type {[Array, Function]} Columnas (Status) */
  const [columns, setColumns] = useState([])

  /** @type {[Object|null, Function]} Ticket en proceso de drag */
  const [dragged, setDragged] = useState(null)

  /** @type {[boolean, Function]} Estado de carga */
  const [loading, setLoading] = useState(false)

  /**
   * Carga inicial de columnas desde la base de datos.
   * Se ejecuta cuando Supabase está disponible.
   */
  useEffect(() => {
    loadColumns()
  }, [])

  /**
   * Obtiene los estados (columnas) desde Supabase.
   *
   * @async
   * @function loadColumns
   * @returns {Promise<void>}
   */
  const loadColumns = async () => {
    if (!supabase) return

    setLoading(true)

    try {
      const { data, error } = await supabase
        .schema('Tickets')
        .from('status')
        .select('*')

      if (error) {
        console.error('Error cargando columnas:', error)
        return
      }

      // Solo cargamos las columnas. La construcción del board la maneja el
      // efecto [tickets, columns] para no llamar a setBoard dos veces seguidas.
      setColumns(data || [])
    } catch (err) {
      console.error('Error inesperado:', err)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Reconstruye el board cuando cambian los tickets o las columnas.
   */
  useEffect(() => {
    if (!columns.length) return
    buildBoard(columns, tickets)
  }, [tickets, columns])

  /**
   * Agrupa los tickets por columna (statusId).
   *
   * @function buildBoard
   * @param {Array} columns - Lista de columnas
   * @param {Array} tickets - Lista de tickets
   */
  const buildBoard = (columns, tickets) => {
    const newBoard = {}

    columns.forEach((col) => {
      const key = col.id

      newBoard[key] = tickets.filter(
        (t) => Number(t.statusId) === Number(col.id)
      )
    })

    setBoard(newBoard)
  }

  /**
   * Maneja el inicio del drag de un ticket.
   *
   * @param {Object} card - Ticket arrastrado
   * @param {number|string} fromColumn - ID de la columna origen
   */
  const handleDragStart = (card, fromColumn) => {
    setDragged({ card, fromColumn })
  }

  /**
   * Maneja el drop del ticket en una nueva columna.
   *
   * @param {number|string} toColumn - ID de la columna destino
   */
  const handleDrop = (toColumn) => {
    if (!dragged) return

    const { card, fromColumn } = dragged

    if (Number(fromColumn) === Number(toColumn)) return

    const newBoard = { ...board }

    newBoard[fromColumn] = newBoard[fromColumn].filter((c) => c.id !== card.id)
    newBoard[toColumn] = [...newBoard[toColumn], card]

    setBoard(newBoard)
    setDragged(null)

    // Log de columna destino
    //const toColumnName = columns.find((col) => col.id === Number(toColumn))?.name ?? toColumn
    //console.log(`Ticket #${card.id} movido a: ${toColumnName}`)

    updateTicketStatus(card.id, toColumn)
  }

  /**
   * Actualiza el estado de un ticket en la base de datos.
   *
   * @async
   * @function updateTicketStatus
   * @param {number|string} id - ID del ticket
   * @param {number|string} statusId - Nuevo estado
   */
  const updateTicketStatus = async (id, statusId) => {
    try {
      const { error } = await supabase
        .schema('Tickets')
        .from('ticket')
        .update({ statusId })
        .eq('id', id)

      if (error) throw error
      // El cierre/finalización notifica al creador vía trigger de base de datos.
    } catch (err) {
      console.error('Error actualizando ticket:', err)
    }
  }

  return {
    board,
    columns,
    loading,
    handleDragStart,
    handleDrop,
    reloadColumns: loadColumns,
  }
}
